import { PAGES, StatusForBroker } from "@dune/types";
import {
	Button,
	PaginatedDataTable,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	useDebounce,
	useSidebar,
} from "@dune/ui";
import { ChevronDown, Plus, SearchSm } from "@untitled-ui/icons-react";
import { FileWarning } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetAllBrokersSocieties } from "../../../api/queries/societies.queries";
import PageLayoutWithTabs from "../../../component/layout/PageLayoutWithTabs";
import { brokerColumns } from "./brokerColumns";

const BrokerList = () => {
	const [searchInput, setSearchInput] = useState("");
	const [statusInput, setStatusInput] = useState("");
	const [page, setPage] = useState(1);
	const [limit, setLimit] = useState(10);
	const navigate = useNavigate();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";

	const debouncedSearch = useDebounce(searchInput, 1000);

	const {
		data: response = {
			data: [],
			meta: {
				page: 1,
				limit: 10,
				total: 0,
				totalPages: 0,
				hasNextPage: false,
				hasPreviousPage: false,
			},
		},
	} = useGetAllBrokersSocieties({
		search: debouncedSearch || undefined,
		status: statusInput || undefined,
		page,
		limit,
	});

	const { data: paginatedData = [], meta: paginationMeta } = response;

	useEffect(() => {
		if (page > paginationMeta.totalPages && paginationMeta.totalPages > 0) {
			setPage(1);
		}
	}, [paginationMeta.totalPages, page]);

	const handleLimitChange = (newLimit: number) => {
		setLimit(newLimit);
		setPage(1);
	};

	return (
		<PageLayoutWithTabs title="Apporteurs" tabContent={{}}>
			<div className="space-y-6 flex-1 min-h-0 flex flex-col">
				{/* Search, Filters and Header Actions aligned */}
				<div className="flex flex-row justify-between items-center gap-4">
					<div className="flex flex-row gap-2 w-1/2">
						<Input
							type="text"
							placeholder="Recherche rapide"
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setSearchInput(e.target.value)
							}
							iconOrText={<SearchSm />}
							iconOrTextPosition="right"
						/>

						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<div className="w-full">
									<Input
										type="text"
										readOnly
										placeholder="Statut"
										className="cursor-pointer"
										value={
											statusInput
												? StatusForBroker[
														statusInput as keyof typeof StatusForBroker
													]
												: ""
										}
										iconOrText={<ChevronDown />}
										iconOrTextPosition="right"
									/>
								</div>
							</DropdownMenuTrigger>

							<DropdownMenuContent className="min-w-[330px]">
								{Object.entries(StatusForBroker).map(([key, value]) => (
									<DropdownMenuItem
										key={key}
										onClick={() => setStatusInput(key)}
									>
										{value}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
					<Button onClick={() => navigate("/auth/orias")}>
						<Plus className="mr-2" /> Nouvel apporteur
					</Button>
				</div>

				{/* Table */}
				<div className="flex-1 min-h-0 w-full overflow-auto">
					<div className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"}`}>
						<PaginatedDataTable
							columns={brokerColumns}
							data={paginatedData}
							meta={paginationMeta}
							onPageChange={setPage}
							onLimitChange={handleLimitChange}
							noResultsText="Aucun apporteur trouvé"
							icon={FileWarning}
							variant="default-with-sort"
							onRowClick={(row) => {
								navigate(PAGES.BROKERS.show.withParams(String(row.id)));
							}}
						/>
					</div>
				</div>
			</div>
		</PageLayoutWithTabs>
	);
};

export default BrokerList;
